import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Loader2, LocateFixed, MapPin, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { SelectField } from '@/components/ui/select';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { AuthImage } from '@/components/auth-image';
import { use_geolocation } from '@/lib/use-geolocation';
import { AdoptTabs } from '@/components/adopt-tabs';
import { use_browse } from '@/features/adoption-listing/hooks';
import { SPECIES_OPTIONS, SPECIES_LABEL, age_label, type AdoptionListing } from '@/features/adoption-listing/types';

function ListingCard({ l, onClick }: { l: AdoptionListing; onClick: () => void }) {
  const primary = l.photos.find((p) => p.is_primary) ?? l.photos[0];
  const age = age_label(l);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`adopt-card-${l.id}`}
      className="group clay-press flex w-full flex-col overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-card text-left shadow-clay transition-all duration-normal ease-bounce hover:-translate-y-1 hover:border-primary/40 hover:shadow-clay-lg"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {primary ? (
          <AuthImage path={`${primary.file_path}?variant=full`} alt={SPECIES_LABEL[l.species]} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center accent-gradient text-white/90"><Heart className="h-10 w-10" /></div>
        )}
        {l.distance_km != null && (
          <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-foreground shadow-clay-sm backdrop-blur-sm" data-testid={`adopt-distance-${l.id}`}>
            <MapPin className="h-3 w-3 text-primary" /> {l.distance_km.toFixed(1)} km
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1 p-3">
        <p className="truncate font-heading text-base font-bold">{SPECIES_LABEL[l.species]}{l.breed ? ` · ${l.breed}` : ''}</p>
        <p className="text-xs text-muted-foreground">{[age, l.gender !== 'UNKNOWN' ? l.gender.toLowerCase() : null, l.city].filter(Boolean).join(' · ')}</p>
        <div className="mt-0.5 flex flex-wrap gap-1">
          {l.vaccinated && <span className="rounded-full bg-success/12 px-2 py-0.5 text-[10px] font-semibold text-success">vaccinated</span>}
          {l.sterilized && <span className="rounded-full bg-info/12 px-2 py-0.5 text-[10px] font-semibold text-info">sterilised</span>}
        </div>
      </div>
    </button>
  );
}

export function AdoptBrowsePage() {
  const navigate = useNavigate();
  const [species, set_species] = useState('');
  const [q_input, set_q_input] = useState('');
  const [q, set_q] = useState('');
  const [city, set_city] = useState('');
  const geo = use_geolocation();
  const use_distance = geo.status === 'granted' && geo.lat != null && geo.lng != null;

  useEffect(() => {
    const t = setTimeout(() => set_q(q_input.trim()), 300);
    return () => clearTimeout(t);
  }, [q_input]);

  const list_q = use_browse({
    species: species || undefined,
    q: q || undefined,
    city: city.trim() || undefined,
    lat: use_distance ? geo.lat! : undefined,
    lng: use_distance ? geo.lng! : undefined,
  });
  const items = list_q.data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="Adopt a pet" description="Give a rescued or homeless pet a loving home." />
      <AdoptTabs />

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search breed or description…" value={q_input} onChange={(e) => set_q_input(e.target.value)} data-testid="adopt-search" />
        </div>
        <SelectField value={species} onValueChange={set_species} placeholder="Any species" className="sm:w-40" data-testid="adopt-filter-species" options={[{ value: '', label: 'Any species' }, ...SPECIES_OPTIONS]} />
        <Input className="sm:w-40" placeholder="City" value={city} onChange={(e) => set_city(e.target.value)} data-testid="adopt-filter-city" />
        <Button
          variant={use_distance ? 'gradient' : 'outline'}
          size="sm"
          onClick={() => geo.request()}
          data-testid="adopt-near-me"
          className="shrink-0"
        >
          <LocateFixed className="h-4 w-4" /> {use_distance ? 'Nearest first' : 'Near me'}
        </Button>
      </div>

      {list_q.isLoading ? (
        <div className="flex items-center justify-center p-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
      ) : list_q.isError ? (
        <ErrorState data-testid="adopt-browse-error" title="Couldn't load listings" description="Please try again." />
      ) : items.length === 0 ? (
        <EmptyState data-testid="adopt-browse-empty" icon={Heart} title="No pets here yet" description="Try a different filter — or be the first to post a pet for adoption." />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="adopt-list">
          {items.map((l) => (
            <li key={l.id}><ListingCard l={l} onClick={() => navigate(`/app/adopt/${l.id}`)} /></li>
          ))}
        </ul>
      )}
    </div>
  );
}
